import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import type {
  AuthUser,
  LoginPayload,
  RegisterPayload,
  MfaMethod,
  LoginMfaResponse,
  MfaVerifyResponse,
  MfaBackupVerifyResponse,
} from '../types';
import { authApi, clearMfaCsrfToken } from '../lib/api';

// ── MFA pending state (session-only, never stored in localStorage) ──────────

interface MfaPendingState {
  userIdHint: string;
  availableMethods: MfaMethod[];
  primaryMethod: MfaMethod | null;
  mfaSetupRequired: boolean;
  /** challenge_id from /challenge/send — only set after email/sms send */
  challengeId: string | null;
}

interface AuthContextValue {
  user: AuthUser | null;
  token: string | null;
  isAuthenticated: boolean;

  /** MFA flow is pending (user logged in credentials but not yet verified 2FA) */
  mfaPending: boolean;
  mfaState: MfaPendingState | null;

  login: (email: string, password: string, remember?: boolean) => Promise<'ok' | 'mfa_challenge' | 'mfa_setup'>;
  completeMfa: (result: MfaVerifyResponse | MfaBackupVerifyResponse) => void;
  cancelMfa: () => void;
  setChallengeId: (id: string) => void;
  logout: () => Promise<void>;
  register: (data: RegisterPayload) => Promise<void>;
  loginDemo: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** Tokens that should never survive an app reload because the v2 backend
 *  doesn't accept them. Leftovers from the old `loginDemo` auto-fallback
 *  pretended the user was signed in while every API call returned 401. */
// Tokens die beim Mount aktiv weggeworfen werden. Historisch hat
// `loginDemo()` Tokens wie `demo-token-…` gesetzt und es gab kurzzeitig
// einen Auto-Fallback der "demo"-Sessions vortäuschte ohne ein echtes
// Backend dahinter — daher die `fake-`-Prefix. Den `demo-token-`-Prefix
// haben wir entfernt, weil unser kanonisches `loginDemo()`-Token genau
// damit anfing und sich selbst beim ersten Re-Mount weggespült hat
// (sichtbar als Integration-Test-Failures + nicht-persistenter Demo-Login).
// Das aktuelle kanonische Demo-Token (siehe loginDemo unten) verwendet
// `sh24-demo-` als Prefix und ist explizit erlaubt.
const STALE_TOKEN_PREFIXES = ['fake-'];

function loadStoredAuth(): { user: AuthUser | null; token: string | null } {
  try {
    const token = localStorage.getItem('sh24.token');
    if (token && STALE_TOKEN_PREFIXES.some(p => token.startsWith(p))) {
      localStorage.removeItem('sh24.token');
      localStorage.removeItem('sh24.user');
      return { token: null, user: null };
    }
    const userRaw = localStorage.getItem('sh24.user');
    const user = userRaw ? (JSON.parse(userRaw) as AuthUser) : null;
    return { token, user };
  } catch {
    return { token: null, user: null };
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  // Lazy initializer (callback form) ensures loadStoredAuth runs exactly
  // once per mount — without it, the stale-token purge fires on every
  // render and wipes any fresh `loginDemo()` token (breaks unit tests).
  const [user, setUser]   = useState<AuthUser | null>(() => loadStoredAuth().user);
  const [token, setToken] = useState<string | null>(() => loadStoredAuth().token);

  // MFA state is session-only — never persisted to localStorage.
  const [mfaState, setMfaState] = useState<MfaPendingState | null>(null);

  const isAuthenticated = token !== null && user !== null;
  const mfaPending = mfaState !== null;

  /**
   * Step 1: submit credentials.
   * Returns:
   *   'ok'            — authenticated directly (no MFA)
   *   'mfa_challenge' — MFA required, user_id_hint stored in state
   *   'mfa_setup'     — MFA setup pending, redirect to wizard
   */
  const login = useCallback(async (
    email: string,
    password: string,
    _remember = true
  ): Promise<'ok' | 'mfa_challenge' | 'mfa_setup'> => {
    const payload: LoginPayload = { email, password };
    const response = await authApi.login(payload);

    if (!response.mfa_required) {
      // Direct auth — set token and user immediately
      const { token: newToken, user: newUser } = response;
      setToken(newToken);
      setUser(newUser);
      localStorage.setItem('sh24.token', newToken);
      localStorage.setItem('sh24.user', JSON.stringify(newUser));
      return 'ok';
    }

    // MFA required — save pending state (session-only).
    // Server-side primary_method (from mfa_settings.is_primary) hat Vorrang
    // ueber die Methods-Reihenfolge: der Challenge-Screen soll die vom User
    // konfigurierte Standard-Methode (TOTP/Email/SMS) vorauswaehlen, nicht
    // blind die erste verfuegbare.
    const mfaResp = response as LoginMfaResponse & { token?: string; user?: AuthUser };
    const methods = mfaResp.mfa_methods_available ?? [];
    const primary = (mfaResp.primary_method && methods.includes(mfaResp.primary_method))
      ? mfaResp.primary_method
      : (methods[0] ?? null);
    setMfaState({
      userIdHint: mfaResp.user_id_hint,
      availableMethods: methods,
      primaryMethod: primary,
      mfaSetupRequired: mfaResp.mfa_setup_pending ?? false,
      challengeId: null,
    });

    // First-time setup: backend issues a Passport token so the wizard can
    // call /me/mfa/setup/* (which is gated by auth:admin). After successful
    // MFA verify the challenge endpoint re-issues a fresh token.
    if (mfaResp.mfa_setup_pending && mfaResp.token && mfaResp.user) {
      setToken(mfaResp.token);
      setUser(mfaResp.user);
      localStorage.setItem('sh24.token', mfaResp.token);
      localStorage.setItem('sh24.user', JSON.stringify(mfaResp.user));
    }

    return (mfaResp.mfa_setup_pending) ? 'mfa_setup' : 'mfa_challenge';
  }, []);

  /** Called after a successful MFA verify — stores auth token and clears MFA state.
   *  N-MEDIUM-01: clears the module-level CSRF token so it can't be replayed. */
  const completeMfa = useCallback((result: MfaVerifyResponse | MfaBackupVerifyResponse) => {
    clearMfaCsrfToken(); // N-MEDIUM-01: discard Step-Up CSRF token
    setToken(result.token);
    setUser(result.user);
    setMfaState(null);
    localStorage.setItem('sh24.token', result.token);
    localStorage.setItem('sh24.user', JSON.stringify(result.user));
  }, []);

  /** Aborts the MFA flow — clears pending state (user remains unauthenticated).
   *  N-MEDIUM-01: also clears CSRF token. */
  const cancelMfa = useCallback(() => {
    clearMfaCsrfToken(); // N-MEDIUM-01: discard Step-Up CSRF token
    setMfaState(null);
  }, []);

  /** Stores the challenge_id returned by /challenge/send into the MFA state */
  const setChallengeId = useCallback((id: string) => {
    setMfaState(prev => prev ? { ...prev, challengeId: id } : null);
  }, []);

  const logout = useCallback(async () => {
    // v2 backend has no /auth/logout endpoint (US-MFA-01 scope) — short-circuit
    // to local-only cleanup. Token is a Passport-issued JWT that expires
    // server-side; nothing else needs revoking client-initiated.
    setToken(null);
    setUser(null);
    setMfaState(null);
    localStorage.removeItem('sh24.token');
    localStorage.removeItem('sh24.user');
  }, []);

  const register = useCallback(async (data: RegisterPayload) => {
    const response = await authApi.register(data);
    void response;
  }, []);

  // Demo login for development without API
  const loginDemo = useCallback(() => {
    const demoUser: AuthUser = {
      id: 1,
      name: 'Anna Schmidt',
      email: 'a.schmidt@senihelp24.de',
      role: 'admin',
    };
    const demoToken = 'demo-token-sh24-2026';
    setToken(demoToken);
    setUser(demoUser);
    setMfaState(null);
    localStorage.setItem('sh24.token', demoToken);
    localStorage.setItem('sh24.user', JSON.stringify(demoUser));
  }, []);

  return (
    <AuthContext.Provider value={{
      user,
      token,
      isAuthenticated,
      mfaPending,
      mfaState,
      login,
      completeMfa,
      cancelMfa,
      setChallengeId,
      logout,
      register,
      loginDemo,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

/** Wie useAuth, aber ohne Fehler außerhalb des Providers (null). */
export function useOptionalAuth(): AuthContextValue | null {
  return useContext(AuthContext);
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
